import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { loginRequest, registerRequest } from "store/features/user/userActions";
import { RootState } from "store/rootReducer";
import { clearRegisterState } from "store/features/user/userSlice";
import {
  FiArrowLeft,
  FiUser,
  FiMail,
  FiPhone,
  FiLock,
  FiCreditCard,
  FiMapPin,
  FiUpload,
  FiX,
  FiCheck,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface RegisterProps {
  isModal?: boolean;
  onSwitch?: () => void;
  onClose?: () => void;
}

const Register: React.FC<RegisterProps> = ({ isModal, onSwitch, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [emailState, setEmailState] = React.useState("");
  const [passwordState, setPasswordState] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  const location = useLocation();
  const from = (location.state as any)?.from || "/";
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    registerLoading,
    registerError,
    registerSuccessMessage,
    registerSuccess,
  } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    if (registerSuccess) {
      dispatch(loginRequest({ email: emailState, password: passwordState }));
      if (isModal && onClose) {
        dispatch(clearRegisterState());
        onClose();
      } else {
        navigate(from, { replace: true });
        dispatch(clearRegisterState());
      }
    }
  }, [
    registerSuccessMessage,
    navigate,
    isModal,
    onClose,
    registerSuccess,
    dispatch,
    emailState,
    passwordState,
    from,
  ]);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    fullName: Yup.string().required("Full name is required"),
    mobile: Yup.string().required("Mobile is required"),
    aadharNumber: Yup.string(),
    panNumber: Yup.string(),
    address: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      mobile: "",
      password: "",
      fullName: "",
      aadharNumber: "",
      panNumber: "",
      address: "",
      profileImage: "",
      imgExtenstion: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        profileImage: values.profileImage || "",
        imgExtenstion: values.imgExtenstion || "",
      };

      setEmailState(payload.email);
      setPasswordState(payload.password);
      dispatch(registerRequest(payload));
    },
  });

  const handleRemoveImage = () => {
    formik.setFieldValue("profileImage", "");
    formik.setFieldValue("imgExtenstion", "");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div
      className={`relative flex w-full flex-col ${
        isModal ? "h-full overflow-hidden" : "min-h-screen"
      } overflow-hidden`}
    >
      {/* ===== Full Background (Fintech themed, light, subtle) ===== */}
      {!isModal && (
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50" />
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-blue-200/40 blur-3xl" />
          <div className="absolute top-1/3 -right-32 h-96 w-96 rounded-full bg-indigo-200/35 blur-3xl" />
          <div className="absolute -bottom-40 left-1/4 h-[28rem] w-[28rem] rounded-full bg-cyan-200/25 blur-3xl" />

          <div
            className="absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1400' height='900' viewBox='0 0 1400 900'%3E%3Cdefs%3E%3CradialGradient id='g' cx='50%25' cy='45%25' r='70%25'%3E%3Cstop offset='0%25' stop-color='%230ea5e9' stop-opacity='0.22'/%3E%3Cstop offset='55%25' stop-color='%236366f1' stop-opacity='0.10'/%3E%3Cstop offset='100%25' stop-color='%23ffffff' stop-opacity='0'/%3E%3C/radialGradient%3E%3C/defs%3E%3Crect width='1400' height='900' fill='url(%23g)'/%3E%3Cg fill='none' stroke='%231e293b' stroke-opacity='0.18'%3E%3Crect x='90' y='120' rx='18' ry='18' width='290' height='180'/%3E%3Crect x='1050' y='160' rx='18' ry='18' width='290' height='180'/%3E%3Crect x='870' y='560' rx='18' ry='18' width='360' height='220'/%3E%3C/g%3E%3Cg fill='none' stroke='%230ea5e9' stroke-opacity='0.30' stroke-width='3'%3E%3Cpath d='M130 710 C260 620, 320 740, 430 650 S650 610, 770 520 S980 540, 1120 420'/%3E%3C/g%3E%3Cg fill='none' stroke='%236366f1' stroke-opacity='0.22' stroke-width='3'%3E%3Cpath d='M160 760 C300 700, 360 790, 480 720 S690 680, 830 610 S1030 620, 1210 520'/%3E%3C/g%3E%3Cg fill='%230ea5e9' fill-opacity='0.22'%3E%3Ccircle cx='430' cy='650' r='6'/%3E%3Ccircle cx='770' cy='520' r='6'/%3E%3Ccircle cx='1120' cy='420' r='6'/%3E%3C/g%3E%3Cg fill='%236366f1' fill-opacity='0.18'%3E%3Ccircle cx='480' cy='720' r='6'/%3E%3Ccircle cx='830' cy='610' r='6'/%3E%3Ccircle cx='1210' cy='520' r='6'/%3E%3C/g%3E%3Cg stroke='%231e40af' stroke-opacity='0.20' stroke-width='3' fill='none' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M980 310 h90'/%3E%3Cpath d='M1060 290 l20 20 -20 20'/%3E%3Cpath d='M1020 360 h-90'/%3E%3Cpath d='M940 340 l-20 20 20 20'/%3E%3C/g%3E%3C/svg%3E\")",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />

          <div className="absolute inset-0 bg-white/35 backdrop-blur-[1px]" />
        </div>
      )}

      {/* Main Form Container - Transparent, Centered, Scrollable */}
      <div
        className={`w-full flex flex-col items-center bg-transparent relative z-10 ${
          isModal
            ? "h-full overflow-y-auto custom-scrollbar"
            : "h-screen overflow-y-auto custom-scrollbar"
        }`}
      >
        <div className={`w-full ${isModal ? "p-6" : "p-8 lg:p-12 xl:p-16"}`}>
          {!isModal && (
            <RouterLink
              to="/"
              className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors font-medium mb-8 w-fit bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full"
            >
              <FiArrowLeft /> Back to Home
            </RouterLink>
          )}

          {/* Glass Card Container */}
          <div className="w-full max-w-lg mx-auto space-y-8 text-center lg:text-left bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-white/20 relative">
            {isModal && onClose && (
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors z-50 rounded-full hover:bg-gray-100"
              >
                <FiX size={24} />
              </button>
            )}

            <div className="text-center">
              <h1
                className={`${
                  isModal ? "text-2xl" : "text-3xl lg:text-4xl"
                } font-extrabold text-gray-900 tracking-tight mb-2`}
              >
                Create Account
              </h1>
              <p className="text-gray-600 text-lg">
                Join us today and experience seamless payments.
              </p>
            </div>

            <form onSubmit={formik.handleSubmit} className="space-y-6">
              {/* Full Name */}
              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-700">
                  Full Name
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-focus-within:text-blue-600 transition-colors">
                    <FiUser size={18} />
                  </div>
                  <input
                    name="fullName"
                    value={formik.values.fullName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder=""
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200/60 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white hover:bg-white/80 focus:bg-white"
                  />
                </div>
                {formik.touched.fullName && formik.errors.fullName && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.fullName}
                  </p>
                )}
              </div>

              {/* Email & Mobile Split */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">
                    Email
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-focus-within:text-blue-600 transition-colors">
                      <FiMail size={18} />
                    </div>
                    <input
                      name="email"
                      type="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="user@example.com"
                      className="block w-full pl-10 pr-3 py-3 border border-gray-200/60 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white hover:bg-white/80 focus:bg-white"
                    />
                  </div>
                  {formik.touched.email && formik.errors.email && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.email}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">
                    Mobile
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-focus-within:text-blue-600 transition-colors">
                      <FiPhone size={18} />
                    </div>
                    <input
                      name="mobile"
                      value={formik.values.mobile}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="+91 98765 43210"
                      className="block w-full pl-10 pr-3 py-3 border border-gray-200/60 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white hover:bg-white/80 focus:bg-white"
                    />
                  </div>
                  {formik.touched.mobile && formik.errors.mobile && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.mobile}
                    </p>
                  )}
                </div>
              </div>

              {/* Password */}
              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-700">
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-focus-within:text-blue-600 transition-colors">
                    <FiLock size={18} />
                  </div>
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Create a strong password"
                    autoComplete="new-password"
                    className="block w-full pl-10 pr-10 py-3 border border-gray-200/60 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white hover:bg-white/80 focus:bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-blue-600 transition-colors cursor-pointer focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <FiEyeOff size={20} />
                    ) : (
                      <FiEye size={20} />
                    )}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.password}
                  </p>
                )}
              </div>

              {/* IDs Split */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">
                    Aadhaar Number
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-focus-within:text-blue-600 transition-colors">
                      <FiCreditCard size={18} />
                    </div>
                    <input
                      name="aadharNumber"
                      value={formik.values.aadharNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="12-digit number"
                      className="block w-full pl-10 pr-3 py-3 border border-gray-200/60 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white hover:bg-white/80 focus:bg-white"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">
                    PAN Number
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-focus-within:text-blue-600 transition-colors">
                      <FiCreditCard size={18} />
                    </div>
                    <input
                      name="panNumber"
                      value={formik.values.panNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="10-digit PAN"
                      className="block w-full pl-10 pr-3 py-3 border border-gray-200/60 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white hover:bg-white/80 focus:bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-700">
                  Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-focus-within:text-blue-600 transition-colors">
                    <FiMapPin size={18} />
                  </div>
                  <input
                    name="address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Your complete address"
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200/60 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white hover:bg-white/80 focus:bg-white"
                  />
                </div>
              </div>

              {/* Profile Image */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Profile Image
                </label>
                <div className="flex items-center gap-4">
                  <div className="relative flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.currentTarget.files?.[0];
                        if (!file) {
                          formik.setFieldValue("profileImage", "");
                          formik.setFieldValue("imgExtenstion", "");
                          return;
                        }
                        const base64 = await fileToBase64(file);
                        const parts = base64.split(",");
                        const pureBase64 =
                          parts.length > 1 ? parts[1] : parts[0];
                        const ext =
                          file.name.split(".").pop()?.toLowerCase() || "";

                        formik.setFieldValue("profileImage", pureBase64);
                        formik.setFieldValue("imgExtenstion", ext);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50/50 transition-all bg-white/80 backdrop-blur-sm"
                    >
                      <FiUpload />
                      {formik.values.imgExtenstion
                        ? "Change Image"
                        : "Upload Profile Picture"}
                    </button>
                  </div>
                  {formik.values.imgExtenstion && (
                    <div className="flex items-center gap-2 bg-blue-50/80 text-blue-700 px-3 py-1.5 rounded-lg text-sm border border-blue-100 backdrop-blur-sm">
                      <span className="font-medium uppercase">
                        {formik.values.imgExtenstion}
                      </span>
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="p-1 hover:bg-blue-100 rounded-full transition-colors"
                      >
                        <FiX size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {(registerError || registerSuccessMessage) && (
                <div
                  className={`mt-6 bg-transparent text-red-700 p-4 rounded-md shadow-sm${
                    registerError
                      ? "bg-red-50/90 border-red-500 text-red-700"
                      : "bg-green-50/90 border-green-500 text-green-700"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {registerSuccessMessage && <FiCheck size={20} />}
                    <p className="font-medium">
                      {registerError ? "Registration Failed" : "Success"}
                    </p>
                  </div>
                  <p className="text-sm mt-1">
                    {registerError || registerSuccessMessage}
                  </p>
                </div>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={registerLoading}
                  className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-purple-600/20 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all active:scale-[0.98] duration-200"
                >
                  {registerLoading ? "Creating Account..." : "Create Account"}
                </button>
              </div>

              <p className="text-center text-sm text-gray-600">
                Already have an account?{" "}
                {isModal ? (
                  <button
                    onClick={onSwitch}
                    className="font-semibold text-blue-600 hover:text-blue-500 transition-colors hover:underline"
                  >
                    Sign in
                  </button>
                ) : (
                  <RouterLink
                    to="/auth"
                    className="font-semibold text-blue-600 hover:text-blue-500 transition-colors hover:underline"
                  >
                    Sign in
                  </RouterLink>
                )}
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
